/**
 * The two tables a marker's appearance name resolves through, and the only function that reads them.
 *
 * Colour and icon stay separate tables because they vary independently; README "Vocabulary" covers why.
 */

import type { ComponentType, SVGProps } from "react";

import type { ColorParams } from "@/features/map-markers/device-marker/types";
import WeatherStationIcon from "@/icons/svgs/assets/weather-station.svg?react";

/** The colours a palette is made of */
type MarkerColors = Required<Pick<ColorParams, "iconBackground" | "border" | "shadow" | "iconColor">>;

export const MARKER_PALETTES = {
    /** Placeholder, pending design review */
    weatherStation: {
        iconBackground: "#9BD9F0",
        border: "#4A9CBF",
        shadow: "#4A9CBF",
        iconColor: "#000000",
    },
} as const satisfies Record<string, MarkerColors>;

export type MarkerPalette = keyof typeof MARKER_PALETTES;

/** The properties of one appearance */
type MarkerGraphics = {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    /** The icon's scale relative to the marker's disc */
    iconScale: number;
    palette: MarkerPalette;
};

export const MARKER_APPEARANCES = {
    /** Placeholder, pending design review */
    weatherStation: {
        icon: WeatherStationIcon,
        iconScale: 0.65,
        palette: "weatherStation",
    },
} as const satisfies Record<string, MarkerGraphics>;

export type MarkerAppearance = keyof typeof MARKER_APPEARANCES;

/**
 * An appearance name resolves to the properties required to draw it
 *
 * The single place the tables above are read, and so the seam an override plugs into. Legacy's notes
 * recolouring is the one such override today, per README "Known divergences".
 */
export const resolveAppearance = (appearance: MarkerAppearance) => {
    const { icon, iconScale, palette } = MARKER_APPEARANCES[appearance];

    return { icon, iconScale, colors: MARKER_PALETTES[palette] };
};
